import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { signOut, useSession } from '../lib/auth-client.js';

/**
 * Coquille du dashboard : garde de session, navigation latérale et zone de
 * contenu.
 *
 * La garde est ici et non dans chaque route : une page protégée oubliée est
 * une fuite, alors qu'une page publique montée par erreur sous cette coquille
 * se voit immédiatement. Elle ne remplace pas le contrôle serveur — toutes les
 * routes `/v1/*` exigent déjà une session (middleware/session-auth.ts). Elle
 * évite un écran d'erreurs 401 là où l'utilisateur attend un écran de
 * connexion.
 *
 * Le lien d'évitement est le premier élément focusable de la page — sans lui,
 * un utilisateur au clavier retraverse toute la navigation à chaque page.
 */
const NAV = [
  { to: '/', label: "Vue d'ensemble", end: true },
  { to: '/keys', label: 'Clés API', end: false },
  { to: '/billing', label: 'Facturation', end: false },
  { to: '/settings', label: 'Réglages', end: false },
] as const;

export function Shell() {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [leaving, setLeaving] = useState(false);

  const disconnect = async (): Promise<void> => {
    setLeaving(true);
    await signOut();
    // Le cache TanStack survivrait à la déconnexion : sans purge, les clés et
    // statistiques du compte précédent réapparaîtraient une fraction de
    // seconde à la connexion suivante.
    queryClient.clear();
    window.location.href = '/login';
  };

  if (isPending) {
    return (
      <div className="grid min-h-screen place-items-center bg-bg">
        <p role="status" className="text-sm text-text-muted">
          Chargement de votre session…
        </p>
      </div>
    );
  }

  if (!session) {
    // `state` permet de revenir sur la page demandée après connexion plutôt
    // que de retomber systématiquement sur l'accueil.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <div className="min-h-screen bg-bg">
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-accent focus:px-4 focus:py-2 focus:text-accent-fg"
      >
        Aller au contenu principal
      </a>

      <div className="mx-auto flex max-w-[1200px] gap-8 px-6 py-8">
        <nav aria-label="Navigation principale" className="flex w-52 shrink-0 flex-col">
          <p className="mb-6 text-lg font-bold tracking-tight">
            <span className="text-text">My</span>
            <span className="text-accent">OG</span>
          </p>
          <ul className="space-y-1">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [
                      'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                      isActive
                        ? 'bg-accent-subtle text-accent'
                        : 'text-text-muted hover:bg-surface hover:text-text',
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="mt-8 space-y-1 border-t border-border pt-4">
            <p className="truncate px-3 text-xs text-text-subtle" title={session.user.email}>
              {session.user.email}
            </p>
            <button
              type="button"
              onClick={() => void disconnect()}
              disabled={leaving}
              className="block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-text-muted transition-colors hover:bg-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
            >
              {leaving ? 'Déconnexion…' : 'Se déconnecter'}
            </button>
          </div>
        </nav>

        <main id="contenu" className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
